import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { postOrQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";
import SafetyMeetingModal from "./SafetyMeetingModal";
import { Play, Square, Coffee, MapPin, MapPinOff, Loader2, X, History, Thermometer, Plane, Pause, Car } from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";
import { useLiveTick } from "@/hooks/useLiveTick";
import { useAuth } from "@/context/AuthContext";
import { PINNED_PROJECTS } from "@/components/dashboard/pinnedProjects";
import PersonalVehicleOption from "@/components/dashboard/PersonalVehicleOption";

const fmtHMS = (secs) => {
  const s = Math.max(0, Math.floor(Number(secs || 0)));
  const h = Math.floor(s / 3600).toString().padStart(2, "0");
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${h}:${m}:${ss}`;
};
const fmtClock = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return iso; }
};
// "HH:MM" in the BUSINESS timezone (America/Toronto) — the backend reads all
// oops/correction times as Toronto time regardless of the device's timezone.
const nowTorontoHM = () =>
  new Date().toLocaleTimeString("en-GB", { timeZone: "America/Toronto", hour12: false, hour: "2-digit", minute: "2-digit" });
const isoToTorontoHM = (iso) =>
  new Date(iso).toLocaleTimeString("en-GB", { timeZone: "America/Toronto", hour12: false, hour: "2-digit", minute: "2-digit" });

// One-shot GPS fetch. Resolves with {lat,lng,accuracy} or {} on any failure —
// we never block the punch on missing location.
const getGps = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve({}),
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 8_000 },
    );
  });

// Punches (clock in/out, lunch start/end) REQUIRE location. This rejects
// when permission is denied, the device location is off, or it times out —
// the caller shows the blocking "Location required" dialog instead of
// punching without coordinates.
const getGpsRequired = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("unsupported"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (err) => reject(err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 },
    );
  });

// Job picker dialog shown on clock-in.
const JobPickDialog = ({ open, jobs, canType, onCancel, onConfirm }) => {
  const [manual, setManual] = useState("");
  const [pickId, setPickId] = useState("");
  useEffect(() => { if (open) { setManual(""); setPickId(""); } }, [open]);
  if (!open) return null;
  const submit = () => {
    if (canType && manual.trim()) { onConfirm({ job_project_id: "", job_name: manual.trim() }); return; }
    if (!pickId) {
      toast.error("Select a project to clock in");
      return;
    }
    const hit = jobs.find((j) => j.project_id === pickId);
    onConfirm({ job_project_id: pickId, job_name: hit?.name || "" });
  };
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" data-testid="clock-job-dialog">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <h3 className="text-lg font-bold uppercase tracking-tight">What job?</h3>
          <button onClick={onCancel} data-testid="clock-job-cancel" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4 p-4">
          {canType && (
            <>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Job not on the list?</label>
                <input
                  value={manual}
                  onChange={(ev) => setManual(ev.target.value)}
                  placeholder="Type a job name…"
                  data-testid="clock-job-manual"
                  className="mt-1 h-10 w-full rounded border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                />
                <p className="mt-1 font-mono text-[10px] text-white/40">(Won&apos;t create a project — just stored on this shift.)</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">or pick from active jobs</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
            </>
          )}
          {!canType && (
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
              Select the project you&apos;re working on — required to clock in.
            </p>
          )}
          <div className="max-h-60 space-y-1 overflow-y-auto rounded border border-white/10">
            {jobs.length === 0 && (
              <p className="p-3 text-center font-mono text-[11px] uppercase tracking-widest text-white/40">No active projects</p>
            )}
            {jobs.map((j) => (
              <button
                key={j.project_id}
                onClick={() => { setPickId(j.project_id); setManual(""); }}
                data-testid={`clock-job-pick-${j.project_id}`}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-white/5 ${pickId === j.project_id ? "bg-[#1a3fd1]/20 text-white" : "text-white/80"}`}
              >
                <span className="min-w-0">
                  <span className="block truncate">{j.name}</span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-white/40">{j.customer || "—"}</span>
                </span>
                {pickId === j.project_id && <span className="font-mono text-[10px] uppercase text-[#a9baf5]">selected</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
          <button onClick={onCancel} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70">Cancel</button>
          <button onClick={submit} data-testid="clock-job-confirm" className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500">
            <Play className="h-3.5 w-3.5" /> Clock in
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ClockCard = () => {
  const { user } = useAuth();
  // Refresh-proof clock: hydrate from a per-session localStorage snapshot so
  // a page refresh keeps the running total on screen instead of flashing
  // 0:00:00 while the server fetch is in flight. Keyed by session token so a
  // different login on the same device never sees someone else's clock.
  const cacheKey = `clock_today_cache:${(localStorage.getItem("session_token") || "anon").slice(-12)}`;
  const [today, setToday] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (c && c.enabled && c.state && c.state !== "idle") return c;
    } catch { /* ignore */ }
    return { enabled: false, state: "idle", events: [], paid_seconds: 0, open_since: null, on_lunch_since: null, current_job: "", day_status: null };
  });
  useEffect(() => {
    try { localStorage.setItem(cacheKey, JSON.stringify(today)); } catch { /* ignore */ }
  }, [today, cacheKey]);
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobDialog, setJobDialog] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false); // daily safety video gate (first clock-in of the day)
  const [safetyGate, setSafetyGate] = useState({ slotKey: "default", videoVersion: 0, picked: null });
  const [oopsOpen, setOopsOpen] = useState(false);
  const [oopsTime, setOopsTime] = useState("");
  const [oopsJobId, setOopsJobId] = useState("");
  const [outConfirm, setOutConfirm] = useState(null); // { entry, paid } after clock-out
  const [outFix, setOutFix] = useState(false);
  const [outTime, setOutTime] = useState("");
  // Manager-only sign-out review: managers see every punch of the day with
  // editable times before the clock-out lands; edits go to an admin.
  const isManager = (user?.role || "") === "manager";
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTimes, setReviewTimes] = useState({}); // entry_id -> "HH:MM" (Toronto)
  const [reviewBusy, setReviewBusy] = useState(false);
  const [locDialog, setLocDialog] = useState(false); // location-off blocking warning
  const [pendingPunch, setPendingPunch] = useState(null); // { event, extras } to retry
  const [tick, setTick] = useState(0);
  const jobsLoaded = useRef(false);
  const confirm = useConfirm();
  // Travel is open to everyone: punching Travel starts a trip (location +
  // reason to the office); punching Arrived ends it and asks which job site
  // they're back at — the drive AND the work after it book to that job.
  const [travelOpen, setTravelOpen] = useState(false);
  const [travelNote, setTravelNote] = useState("");
  const [travelJobId, setTravelJobId] = useState(""); // required: the job the trip is FOR
  const [arriveOpen, setArriveOpen] = useState(false);
  const [arriveQuery, setArriveQuery] = useState("");
  const [arriveSel, setArriveSel] = useState(null); // { project_id, name } | { manual } | null (no job)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/clock/today");
      setToday(data);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useLiveTick(load); // impersonation live-watch: refetch on every tick

  // Poll every 60s so the server-side timesheet sync ticks over — that's how
  // the weekly Hours grid gains its next 0.25 h without any punching.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // Live-tick display every second while on-clock; a slow tick while idle
  // keeps the "paid from" preview on the Clock-in button current.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), today.state === "idle" ? 15000 : 1000);
    return () => clearInterval(id);
  }, [today.state]);

  // "Clock in now → paid from HH:MM": punch judged by the minute, + lead
  // minutes, up to the next quarter mark (exact punch when the window
  // doesn't apply to this person / this punch).
  const paidFromPreview = useMemo(() => {
    if (today.state !== "idle" || !today.enabled) return null;
    const now = new Date();
    if (!today.paid_from_window_applies) return { label: fmtClock(now.toISOString()), waitMin: 0 };
    const base = new Date(now); base.setSeconds(0, 0); base.setMinutes(base.getMinutes() + (today.early_punch_lead_minutes ?? 10));
    const q = new Date(base); q.setMinutes(Math.ceil(base.getMinutes() / 15) * 15, 0, 0);
    return { label: fmtClock(q.toISOString()), waitMin: Math.max(0, Math.round((q - now) / 60000)) };
  }, [today.state, today.enabled, today.paid_from_window_applies, today.early_punch_lead_minutes, tick]); // eslint-disable-line

  const loadJobs = async () => {
    if (jobsLoaded.current) return;
    try {
      const { data } = await api.get("/projects");
      // Office / Estimating are pinned pseudo-jobs — always selectable.
      setJobs([...PINNED_PROJECTS, ...(data || []).filter((p) => (p.status || "Active") === "Active" || p.status === "In Progress")]);
      jobsLoaded.current = true;
    } catch { /* ignore */ }
  };

  // Live PAID-seconds counter: the server gives paid_seconds at the moment of
  // the last fetch; we add the delta since then while paid time is accruing.
  // The big timer matches PAID time exactly — warmup minutes before the
  // quarter-hour start don't count (the card shows a countdown instead), and
  // the timer freezes past the paid lunch/break grace.
  const [baseAt] = useState({ ref: Date.now() });
  useEffect(() => { baseAt.ref = Date.now(); }, [today.paid_seconds, today.elapsed_seconds, today.state, baseAt]);

  const liveSeconds = useMemo(() => {
    void tick;
    const nowMs = Date.now();
    const base = today.paid_seconds ?? 0;
    // Only accruing states tick; anything else shows the frozen paid total.
    if (today.state !== "in" && today.state !== "lunch" && today.state !== "break") return base;
    // Paid time starts at the rounded quarter-hour work start, not the raw
    // punch — if the last fetch happened during warmup, don't count the wait.
    const workStartMs = today.work_start ? new Date(today.work_start).getTime() : null;
    const paidFromMs = workStartMs && workStartMs > baseAt.ref ? workStartMs : baseAt.ref;
    const delta = Math.max(0, nowMs - paidFromMs) / 1000;
    if (today.state === "in") return base + delta;
    // On break → the paid clock keeps ticking for the first 15 minutes,
    // then freezes until the employee ends the break (mirrors the server).
    if (today.state === "break") {
      if (!today.on_break_since) return base + delta;
      const graceEndsMs = new Date(today.on_break_since).getTime() + 15 * 60 * 1000;
      const cappedDeltaMs = Math.max(0, Math.min(nowMs, graceEndsMs) - paidFromMs);
      return base + cappedDeltaMs / 1000;
    }
    // On lunch → same, with the employee's paid-lunch grace (30/60 min).
    if (!today.on_lunch_since) return base + delta;
    const capSec = (today.paid_lunch_minutes || 30) * 60;
    const graceEndsMs = new Date(today.on_lunch_since).getTime() + capSec * 1000;
    const cappedDeltaMs = Math.max(0, Math.min(nowMs, graceEndsMs) - paidFromMs);
    return base + cappedDeltaMs / 1000;
  }, [tick, today, baseAt]);

  // 8-hour PAID cap (employee role only): the on-screen paid timer FREEZES
  // at 8:00:00 while the clock keeps counting internally. The OT request
  // fires at 8h15; approving releases the clamp — the timer jumps to the
  // real total (the wait time counts) and resumes. Denied stays at 8:00.
  const OT_CAP_SECONDS = 8 * 3600;
  const otFrozen = user?.role === "employee"
    && today.overtime_status !== "approved"
    && liveSeconds >= OT_CAP_SECONDS;
  const displaySeconds = otFrozen ? OT_CAP_SECONDS : liveSeconds;

  // Paid break/lunch countdown windows: while a break runs, its End button
  // becomes a depleting progress bar. Parent re-renders every second (tick),
  // so the bar + countdown text stay live without their own timer.
  const BREAK_ALLOT_SEC = 15 * 60;
  const lunchAllotSec = (today.paid_lunch_minutes || 30) * 60;
  const paidWindow = (since, allotSec) => {
    if (!since) return null;
    const remain = Math.max(0, allotSec - (Date.now() - new Date(since).getTime()) / 1000);
    return { remain, frac: Math.max(0, Math.min(1, remain / allotSec)) };
  };
  const fmtMMSS = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const breakWin = today.state === "break" ? paidWindow(today.on_break_since, BREAK_ALLOT_SEC) : null;
  const lunchWin = today.state === "lunch" ? paidWindow(today.on_lunch_since, lunchAllotSec) : null;

  // Still inside the quarter-hour warmup? Show a countdown to the paid start
  // instead of a running timer (re-computed every tick in render below).
  const workStartMs = today.work_start ? new Date(today.work_start).getTime() : null;
  const warmupPending = today.state === "in" && !!workStartMs && workStartMs > Date.now();

  const punch = async (event, extras = {}) => {
    setBusy(true);
    try {
      // Location is mandatory for every punch — if it's off/denied, block
      // the punch and show the "Location required" dialog (with retry).
      let gps;
      try {
        gps = await getGpsRequired();
      } catch (_geoErr) {
        setPendingPunch({ event, extras });
        setLocDialog(true);
        return;
      }
      const res = await postOrQueue("/me/clock", { event, ...gps, ...extras }, `${event.replace("_", " ")} punch`);
      if (res.queued) { toast.warning("No signal — your punch is saved and will be sent automatically when you're back online"); return; }
      const { data } = res;
      setToday(data);
      if (event === "clock_in") toast.success(`Clocked in${extras.job_name ? " · " + extras.job_name : ""}${(data.warmup_remaining || 0) > 0 ? ` — hours begin ${fmtClock(data.work_start)}` : ""}`);
      else if (event === "clock_out") {
        // Confirm the stop time — if they forgot to clock out earlier, they
        // can correct it right here (same day only).
        setOutFix(false);
        setOutTime(isoToTorontoHM(data.entry.timestamp));
        setOutConfirm({ entry: data.entry, paid: data.paid_seconds });
      }
      else if (event === "lunch_start") {
        // Ask (once) for a browser Notification permission so the lunch-end
        // alert can pop even if the tab is backgrounded.
        try { if ("Notification" in window && Notification.permission === "default") Notification.requestPermission(); } catch (_) { /* ignore */ }
        toast.success(`Lunch started — clock keeps ticking for ${data.paid_lunch_minutes || 30} min`);
      }
      else if (event === "lunch_end") toast.success("Back from lunch");
      else if (event === "break_start") toast.success("Break started — the first 15 minutes are paid");
      else if (event === "break_end") toast.success("Back from break");
      else if (event === "travel") toast.success(`Travel started${extras.job_name ? ` — booking to ${extras.job_name}` : ""} — tap Arrived when you're back on site`);
      else if (event === "travel_end") toast.success(
        extras.job_project_id ? `Arrived — hours now tracking to ${extras.job_name}`
          : extras.job_name ? `Arrived — "${extras.job_name}" logged (hours stay on the current job)`
          : "Arrived — travel ended"
      );
      // Refetch the full day payload so cap/overtime/break fields (only on
      // /me/clock/today, not the punch response) stay current.
      load();
    } catch (e) {
      // The punch POST can fail at the network level (lost response, proxy
      // hiccup) even though the server RECORDED the punch — the user then
      // sees "Punch failed" while the countdown keeps running. Refetch the
      // day: if its state already reflects the punch (e.g. clock-out →
      // idle), treat it as success and carry on instead of crying failure.
      try {
        const { data: fresh } = await api.get("/me/clock/today");
        const landed =
          (event === "clock_out" && fresh.state === "idle") ||
          (event === "clock_in" && fresh.state !== "idle") ||
          (event === "lunch_start" && fresh.state === "lunch") ||
          (event === "lunch_end" && fresh.state === "in") ||
          (event === "break_start" && fresh.state === "break") ||
          (event === "break_end" && fresh.state === "in");
        if (landed) { setToday(fresh); return; }
      } catch (_recheckErr) { /* fall through to the honest error */ }
      toast.error(e?.response?.data?.detail || "Punch failed");
    } finally { setBusy(false); }
  };

  // Fire an in-app + native browser notification when the paid-lunch window
  // elapses. If the employee is on 30 min we also remind them they may take
  // the extra 30 min unpaid.
  const alertedRef = useRef(false);
  useEffect(() => {
    // Reset the "already alerted" flag when a new lunch begins.
    if (today.state !== "lunch") { alertedRef.current = false; return undefined; }
    if (!today.on_lunch_since) return undefined;
    const cap = (today.paid_lunch_minutes || 30) * 60;
    const started = new Date(today.on_lunch_since).getTime();
    const check = () => {
      if (alertedRef.current) return;
      const elapsed = (Date.now() - started) / 1000;
      if (elapsed < cap) return;
      alertedRef.current = true;
      const isHalf = (today.paid_lunch_minutes || 30) === 30;
      const title = isHalf ? "Paid lunch is over" : "Lunch is over";
      const body = isHalf
        ? "Your 30-minute paid lunch has ended. You can take the extra half-hour on your own time — the clock is paused until you tap End lunch."
        : "Your 1-hour paid lunch has ended. The clock is paused until you tap End lunch.";
      toast.warning(title, { description: body, duration: 15_000 });
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(title, { body, tag: "lunch-over" });
        }
      } catch (_) { /* ignore */ }
    };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [today.state, today.on_lunch_since, today.paid_lunch_minutes]);

  const proceedClockIn = async () => {
    await loadJobs();
    setJobDialog(true);
  };
  // First clock-in of the day: the safety meeting video must be confirmed first.
  // Per-job videos are only known after the job picker, so that mode gates on job confirm instead.
  const safetyCheck = async (project_id) => {
    try {
      const { data } = await api.get("/safety/status", { params: { tz_offset_min: new Date().getTimezoneOffset(), project_id } });
      return data || {};
    } catch { return {}; /* safety service unavailable → don't block the punch */ }
  };
  const onClockIn = async () => {
    const st = await safetyCheck();
    if (st.needs_today) { setSafetyGate({ slotKey: st.slot_key, videoVersion: st.video_version, quiz: st.quiz, picked: null }); setSafetyOpen(true); return; }
    await proceedClockIn();
  };
  const onJobPicked = async (picked) => {
    setJobDialog(false);
    const st = await safetyCheck(picked?.job_project_id || "");
    if (st.needs_today) { setSafetyGate({ slotKey: st.slot_key, videoVersion: st.video_version, quiz: st.quiz, picked }); setSafetyOpen(true); return; }
    punch("clock_in", picked);
  };

  // "Oops — forgot to clock in": backdate the punch to the stated arrival
  // time (today only). The timer starts immediately; admins get a punch
  // request to confirm or correct it.
  const submitOops = async () => {
    if (!oopsTime) {
      toast.error("Pick the time you got to site");
      return;
    }
    const job = jobs.find((j) => j.project_id === oopsJobId);
    if (!job) {
      toast.error("Select the job site you were at");
      return;
    }
    setBusy(true);
    try {
      const gps = await getGps();
      const { data } = await api.post("/me/clock/oops", { time: oopsTime, job_project_id: job.project_id, job_name: job.name, ...gps });
      setToday(data);
      setOopsOpen(false);
      toast.success("Clocked in from " + fmtClock(data.open_since) + " — your admin will confirm the time");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oops punch failed");
    } finally { setBusy(false); }
  };

  // Correct the clock-out time ("I forgot to clock out earlier").
  const submitOutFix = async () => {
    if (!outTime) {
      toast.error("Pick the time you actually stopped");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/me/clock/oops-out", { entry_id: outConfirm.entry.entry_id, time: outTime });
      setToday(data);
      toast.success(`Clock-out corrected to ${outTime}`);
      setOutConfirm(null);
      setOutFix(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not correct the time");
    } finally { setBusy(false); }
  };

  // Retry the blocked punch once the employee has turned location on.
  const retryPendingPunch = () => {
    const p = pendingPunch;
    setLocDialog(false);
    setPendingPunch(null);
    if (p) punch(p.event, p.extras);
  };

  // Manager sign-out review: open the editable punch list (pre-clock-out).
  const openReview = () => {
    const map = {};
    (today.events || []).forEach((e) => { map[e.entry_id] = isoToTorontoHM(e.timestamp); });
    setReviewTimes(map);
    setReviewOpen(true);
  };

  // Save = send any changed punch times to an admin as an edit request,
  // then complete the clock-out. "Not done work" simply closes the dialog.
  const submitReview = async () => {
    setReviewBusy(true);
    try {
      const edits = (today.events || [])
        .map((e) => ({ entry_id: e.entry_id, time: reviewTimes[e.entry_id] }))
        .filter((x) => x.time && x.time !== isoToTorontoHM((today.events || []).find((e) => e.entry_id === x.entry_id)?.timestamp));
      if (edits.length > 0) {
        await api.post("/me/clock/day-edit-request", { edits });
        toast.success("Your time edits were sent to an admin for approval");
      }
      setReviewOpen(false);
      await punch("clock_out");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the edit request");
    } finally { setReviewBusy(false); }
  };

  // Sick / Away: both mark the day SICK on the Hours grid.
  const markDay = async (status) => {
    const label = status === "sick" ? "Sick" : "Away";
    const ok = await confirm({
      title: `Mark today as ${label}?`,
      description: status === "sick"
        ? "Today will be marked SICK on your hours. You won't be clocked in."
        : "Today will be marked as away on your hours. You won't be clocked in.",
      confirmLabel: `Mark ${label}`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/me/day-status", { status });
      toast.success(status === "sick" ? "Marked sick for today — feel better" : "Marked away for today");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark the day");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="stats-clock-card" style={{ display: today.enabled ? undefined : "none" }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">Time clock</h3>
          {warmupPending ? (
            <>
              <p className="mt-1 animate-pulse font-mono text-3xl font-bold text-red-500" data-testid="clock-warmup-countdown">
                -{fmtHMS((workStartMs - Date.now()) / 1000)}
              </p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-red-400" data-testid="clock-warmup-note">
                Paid time starts {fmtClock(today.work_start)} — this countdown is not paid
              </p>
            </>
          ) : (
            <p className="mt-1 font-mono text-3xl font-bold text-white" data-testid="clock-today-total">
              {fmtHMS(displaySeconds)}
            </p>
          )}
          {/* The day this timer is counting for (business day, Toronto). */}
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0]" data-testid="clock-counting-date">
            {(() => {
              const d = today.day || new Date().toISOString().slice(0, 10);
              const dt = new Date(`${d}T12:00:00`); // noon — avoids tz edge flips
              return `Counting ${dt.toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric", year: "numeric" })}`;
            })()}
          </p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-white/40">
            {today.state === "in" && (
              <>on the clock since {fmtClock(today.open_since)}{today.current_job ? ` · ${today.current_job}` : ""}</>
            )}
            {today.state === "lunch" && (
              <>on lunch since {fmtClock(today.on_lunch_since)} ({today.paid_lunch_minutes || 30}-min paid grace)</>
            )}
            {today.state === "break" && (
              <>on break since {fmtClock(today.on_break_since)} (first 15 min paid)</>
            )}
            {today.state === "idle" && (
              <>ready to clock in · {today.paid_lunch_minutes || 30}-min paid lunch · 15-min paid break</>
            )}
          </p>
          {(today.paid_lunch_minutes || 30) === 30 && today.state !== "lunch" && (
            <p className="mt-1 text-[11px] text-white/50">
              You can take a full hour if you need it — only the first 30 min is paid; the rest is on your own time.
            </p>
          )}
        </div>
      </div>

      {today.state === "break" && today.break_over && (
        <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300" data-testid="clock-break-over">
          Past 15 minutes — you&apos;re on your own time until you end the break
        </p>
      )}
      {today.travel_since && (
        <p className="mt-2 rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-[#a9baf5]" data-testid="clock-travelling-banner">
          Travelling since {fmtClock(today.travel_since)} — tap Arrived when you&apos;re back on site
        </p>
      )}
      {(today.cap_stopped || otFrozen) && (
        <div
          className={`mt-2 rounded-md border px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest ${today.cap_stopped || today.overtime_status === "denied" ? "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300" : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"}`}
          data-testid="clock-cap-banner"
        >
          8 paid hours reached — paid clock paused{otFrozen && " (still tracking internally)"}
          {today.overtime_status === "pending" && " · overtime request submitted — waiting on approval"}
          {today.overtime_status === "approved" && " · overtime approved — clock back in to keep going"}
          {today.overtime_status === "denied" && " · overtime denied — payable time stays at 8 hours"}
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 gap-2">
        {today.state === "idle" ? (
          <button
            onClick={onClockIn} disabled={busy}
            data-testid="clock-in-btn"
            className="col-span-3 inline-flex flex-col items-center justify-center gap-0.5 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
          >
            <span className="inline-flex items-center gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Clock in</span>
            {/* Paid-from preview: same rule the server stamps (10 min lead → next
                quarter mark, by the minute); recomputed live each second. */}
            {paidFromPreview && (
              <span className="font-mono text-[10px] font-normal uppercase tracking-widest text-white/85" data-testid="clock-in-paid-from-preview">
                Clock in now → paid from {paidFromPreview.label}{paidFromPreview.waitMin > 0 ? ` (${paidFromPreview.waitMin} min unpaid wait)` : ""}
              </span>
            )}
          </button>
        ) : (
          <>
            <button
              disabled
              data-testid="clock-in-btn-disabled"
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600/25 px-3 py-3 text-sm font-semibold text-emerald-800/70 dark:text-emerald-200/60"
            >
              <Play className="h-4 w-4" /> In
            </button>
            <button
              onClick={() => punch(today.state === "lunch" ? "lunch_end" : "lunch_start")}
              disabled={busy || (today.state !== "lunch" && (today.lunch_used || today.state === "break"))}
              data-testid="clock-lunch-btn"
              title={today.lunch_used && today.state !== "lunch" ? "Lunch already taken today" : undefined}
              className={`relative inline-flex items-center justify-center gap-1.5 overflow-hidden rounded-md px-3 py-3 text-sm font-semibold text-white disabled:opacity-60 ${today.state === "lunch" ? "bg-[#1a3fd1]/25" : "bg-[#1a3fd1]/80 hover:bg-[#1a3fd1]"}`}
            >
              {today.state === "lunch" && lunchWin && (
                <span
                  className="absolute inset-y-0 left-0 bg-[#1a3fd1] transition-[width] duration-1000 ease-linear"
                  style={{ width: `${lunchWin.frac * 100}%` }}
                  data-testid="lunch-progress-fill"
                />
              )}
              <span className="relative z-10 inline-flex items-center gap-1.5">
                <Coffee className="h-4 w-4" />
                {today.state === "lunch"
                  ? (lunchWin && lunchWin.remain > 0 ? `End lunch · ${fmtMMSS(lunchWin.remain)} paid left` : "End lunch")
                  : today.lunch_used ? "Lunch used" : "Lunch"}
              </span>
            </button>
            <button
              onClick={() => (isManager ? openReview() : punch("clock_out"))}
              disabled={busy}
              data-testid="clock-out-btn"
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-red-600 px-3 py-3 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60"
            >
              <Square className="h-4 w-4" /> Out
            </button>
          </>
        )}
      </div>

      {today.state !== "idle" && (
        <div className="mt-2 grid grid-cols-2 gap-2" data-testid="clock-break-row">
          <button
            onClick={() => punch(today.state === "break" ? "break_end" : "break_start")}
            disabled={busy || (today.state !== "break" && (today.break_used || today.state === "lunch"))}
            data-testid="clock-break-btn"
            title={today.break_used && today.state !== "break" ? "Break already used today" : "One paid 15-minute break per day — the rest is on your own time"}
            className={`relative inline-flex items-center justify-center gap-1.5 overflow-hidden rounded-md px-3 py-2.5 text-xs font-semibold disabled:opacity-60 ${
              today.state === "break"
                ? "bg-[#1a3fd1]/25 text-white"
                : "border border-white/20 bg-white/5 text-white/80 hover:bg-white/10"
            }`}
          >
            {today.state === "break" && breakWin && (
              <span
                className="absolute inset-y-0 left-0 bg-[#1a3fd1] transition-[width] duration-1000 ease-linear"
                style={{ width: `${breakWin.frac * 100}%` }}
                data-testid="break-progress-fill"
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1.5">
              <Pause className="h-3.5 w-3.5" />
              {today.state === "break"
                ? (breakWin && breakWin.remain > 0 ? `End break · ${fmtMMSS(breakWin.remain)} paid left` : "End break")
                : today.break_used ? "Break used" : "Break (15 min paid)"}
            </span>
          </button>
          {today.travel_since ? (
            <button
              onClick={() => {
                loadJobs();
                setArriveQuery("");
                // Pre-select the job the trip was started for — the driver
                // can still change it (or pick "No job") on arrival.
                setArriveSel(today.travel_job_project_id
                  ? { project_id: today.travel_job_project_id, name: today.travel_job_name || "" }
                  : null);
                setArriveOpen(true);
              }}
              disabled={busy || today.state !== "in"}
              data-testid="clock-arrive-btn"
              title="Back on site — end the travel and pick the job you're at"
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-2.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-60"
            >
              <MapPin className="h-3.5 w-3.5" /> Arrived
            </button>
          ) : (
            <button
              onClick={() => { loadJobs(); setTravelJobId(""); setTravelOpen(true); }}
              disabled={busy}
              data-testid="clock-travel-btn"
              title="Start travelling — the clock keeps running, and when you tap Arrived you'll pick the job site you're at"
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-3 py-2.5 text-xs font-semibold text-[#a9baf5] hover:bg-[#1a3fd1]/20 disabled:opacity-60"
            >
              <Car className="h-3.5 w-3.5" /> Travel
            </button>
          )}
        </div>
      )}

      {today.state === "idle" && !today.day_status && (
        <div className="mt-2 grid grid-cols-3 gap-2" data-testid="clock-extras-row">
          <button
            onClick={() => {
              loadJobs();
              setOopsTime(nowTorontoHM());
              setOopsJobId("");
              setOopsOpen(true);
            }}
            disabled={busy}
            data-testid="clock-oops-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 dark:text-amber-300 disabled:opacity-60"
          >
            <History className="h-3.5 w-3.5" /> Oops
          </button>
          <button
            onClick={() => markDay("sick")}
            disabled={busy}
            data-testid="clock-sick-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-500/20 dark:text-red-300 disabled:opacity-60"
          >
            <Thermometer className="h-3.5 w-3.5" /> Sick
          </button>
          <button
            onClick={() => markDay("away")}
            disabled={busy}
            data-testid="clock-away-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/20 bg-white/5 px-3 py-2 text-xs font-semibold text-white/70 hover:bg-white/10 disabled:opacity-60"
          >
            <Plane className="h-3.5 w-3.5" /> Away
          </button>
        </div>
      )}
      {today.state === "idle" && today.day_status && (
        <p className="mt-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-white/60" data-testid="clock-day-status">
          Marked {today.day_status} for today
        </p>
      )}
      {/* Personal Vehicle (+2h) — only for users with the opt-in permission */}
      <PersonalVehicleOption enabled={!!user?.permissions?.personal_vehicle} onLogged={load} />
      {today.events.length > 0 && (
        <details className="mt-3 rounded border border-white/5 bg-white/[0.02]">
          <summary className="cursor-pointer px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/40">
            Today&apos;s punches ({today.events.length})
          </summary>
          <ul className="divide-y divide-white/5 text-[11px]">
            {today.events.map((e) => (
              <li key={e.entry_id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                <span className="font-mono text-white/70">{fmtClock(e.timestamp)}</span>
                <span className="font-mono uppercase tracking-widest text-white/60">{e.event.replace("_", " ")}</span>
                {e.lat != null && e.lng != null ? (
                  <a href={`https://maps.google.com/?q=${e.lat},${e.lng}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-[10px] text-[#8fa4f0] hover:underline">
                    <MapPin className="h-3 w-3" /> map
                  </a>
                ) : <span className="font-mono text-[10px] text-white/30">no gps</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      <SafetyMeetingModal open={safetyOpen} slotKey={safetyGate.slotKey} videoVersion={safetyGate.videoVersion} quiz={!!safetyGate.quiz} projectId={safetyGate.picked?.job_project_id || null} onClose={() => setSafetyOpen(false)}
        onConfirmed={async () => { setSafetyOpen(false); toast.success("Safety meeting confirmed — clocking you in"); if (safetyGate.picked) punch("clock_in", safetyGate.picked); else await proceedClockIn(); }} />
      <JobPickDialog
        open={jobDialog}
        jobs={jobs}
        canType={(user?.role || "employee") !== "employee"}
        onCancel={() => setJobDialog(false)}
        onConfirm={onJobPicked}
      />

      {travelOpen && createPortal(
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/75 p-3" data-testid="travel-note-dialog" onClick={() => !busy && setTravelOpen(false)}>
          <div className="w-full max-w-sm overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 text-[#8fa4f0]">
                  <Car className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-bold uppercase tracking-tight" data-testid="travel-note-title">Start travel</h3>
              </div>
              <button onClick={() => setTravelOpen(false)} aria-label="Close" data-testid="travel-note-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 p-4">
              <p className="text-xs leading-relaxed text-white/60">
                Your clock keeps running while you drive — the drive time books to the job you&apos;re travelling to. Tap <span className="font-semibold text-white">Arrived</span> when you&apos;re back on site.
              </p>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Which job are you traveling for?</label>
                <select
                  value={travelJobId}
                  onChange={(e) => setTravelJobId(e.target.value)}
                  data-testid="travel-job-select"
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none [&>option]:bg-white [&>option]:text-gray-900"
                >
                  <option value="">— Select a project —</option>
                  {jobs.map((j) => (
                    <option key={j.project_id} value={j.project_id}>{j.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Why are you traveling?</label>
                <input
                  value={travelNote}
                  onChange={(e) => setTravelNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") document.getElementById("travel-note-submit-el")?.click(); }}
                  placeholder='e.g. "Heading to Pakenham for material pickup"'
                  data-testid="travel-note-input"
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                />
              </div>
              <button
                id="travel-note-submit-el"
                onClick={async () => {
                  const note = travelNote.trim();
                  const job = jobs.find((j) => j.project_id === travelJobId);
                  if (!job) { toast.error("Select the job you're traveling for"); return; }
                  if (!note) { toast.error("Add a short reason for the travel"); return; }
                  setTravelOpen(false);
                  setTravelNote("");
                  setTravelJobId("");
                  await punch("travel", { note, job_project_id: job.project_id, job_name: job.name });
                }}
                disabled={busy}
                data-testid="travel-note-submit"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Car className="h-4 w-4" />} Start travel
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {arriveOpen && createPortal(
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/75 p-3" data-testid="arrive-dialog" onClick={() => !busy && setArriveOpen(false)}>
          <div className="w-full max-w-sm overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                  <MapPin className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-bold uppercase tracking-tight" data-testid="arrive-title">Travel done</h3>
              </div>
              <button onClick={() => setArriveOpen(false)} aria-label="Close" data-testid="arrive-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 p-4">
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Which job site are you back at?</label>
                <input
                  value={arriveQuery}
                  onChange={(e) => setArriveQuery(e.target.value)}
                  placeholder="Search active jobs…"
                  data-testid="arrive-job-search"
                  autoFocus
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-500 focus:outline-none"
                />
                {(() => {
                  const q = arriveQuery.trim().toLowerCase();
                  const matches = jobs.filter((j) => (j.name || "").toLowerCase().includes(q)).slice(0, 20);
                  const exact = jobs.some((j) => (j.name || "").trim().toLowerCase() === q);
                  const rowCls = (active) => `block w-full border-b border-white/5 px-3 py-2 text-left text-sm last:border-b-0 ${active ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-200" : "text-white/80 hover:bg-white/5"}`;
                  return (
                    <div className="mt-1.5 max-h-44 overflow-y-auto rounded-md border border-white/10" data-testid="arrive-job-list">
                      <button type="button" onClick={() => setArriveSel(null)} data-testid="arrive-job-none" className={rowCls(arriveSel === null)}>
                        No job — back at the shop / office
                      </button>
                      {matches.map((j) => (
                        <button key={j.project_id} type="button" onClick={() => setArriveSel({ project_id: j.project_id, name: j.name })}
                          data-testid={`arrive-job-option-${j.project_id}`} className={rowCls(arriveSel?.project_id === j.project_id)}>
                          {j.name}
                        </button>
                      ))}
                      {q && !exact && (
                        <button type="button" onClick={() => setArriveSel({ manual: arriveQuery.trim() })} data-testid="arrive-job-manual" className={rowCls(!!arriveSel?.manual)}>
                          Manual entry: &ldquo;{arriveQuery.trim()}&rdquo;
                        </button>
                      )}
                      {q && matches.length === 0 && !exact && null}
                    </div>
                  );
                })()}
                {arriveSel && (
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-300" data-testid="arrive-job-hint">
                    {arriveSel.project_id
                      ? "The trip and your hours from here book to this job"
                      : `Logged as "${arriveSel.manual}" — hours stay on the current job`}
                  </p>
                )}
              </div>
              <button
                onClick={async () => {
                  const sel = arriveSel;
                  setArriveOpen(false);
                  setArriveSel(null);
                  setArriveQuery("");
                  await punch("travel_end", {
                    ...(sel?.project_id ? { job_project_id: sel.project_id, job_name: sel.name } : {}),
                    ...(sel?.manual ? { job_name: sel.manual } : {}),
                  });
                }}
                disabled={busy}
                data-testid="arrive-submit"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Arrived — track this job
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {locDialog && createPortal(
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/75 p-3" data-testid="clock-location-blocked">
          <div className="w-full max-w-md overflow-hidden rounded-lg border border-red-500/40 bg-[#0b0b0f] text-white">
            <div className="flex items-center gap-3 border-b border-red-500/20 bg-red-500/[0.07] px-4 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-red-500/40 bg-red-500/15 text-red-400">
                <MapPinOff className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-tight text-red-200" data-testid="clock-location-title">Location required</h3>
            </div>
            <div className="space-y-2 p-4">
              <p className="text-sm leading-relaxed text-white/80" data-testid="clock-location-text">
                You must turn on your device&apos;s location and allow this app to use it.
                You can&apos;t clock in, clock out, or take lunch without it.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Turn location on, tap Allow when prompted, then try again
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
              <button
                onClick={() => { setLocDialog(false); setPendingPunch(null); }}
                data-testid="clock-location-cancel"
                className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70"
              >
                Cancel
              </button>
              <button
                onClick={retryPendingPunch}
                data-testid="clock-location-retry"
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-500"
              >
                <MapPin className="h-3.5 w-3.5" /> Try again
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {reviewOpen && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" data-testid="clock-review-dialog">
          <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md border border-red-500/40 bg-red-500/10 text-red-500 dark:text-red-300">
                  <Square className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-bold uppercase tracking-tight" data-testid="clock-review-title">Review your day</h3>
              </div>
              <button onClick={() => setReviewOpen(false)} aria-label="Close" data-testid="clock-review-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 overflow-y-auto p-4">
              <p className="text-xs leading-relaxed text-white/60">
                Check today&apos;s punches before you sign out. If a time isn&apos;t right, fix it here —
                your changes go to an admin for approval.
              </p>
              <p className="rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-3 py-2 text-center font-mono text-xs text-[#8fa4f0]" data-testid="clock-review-total">
                Total today: <span className="font-bold text-white">{fmtHMS(liveSeconds)}</span> paid
              </p>
              <ul className="space-y-1.5" data-testid="clock-review-list">
                {(today.events || []).map((e) => (
                  <li key={e.entry_id} data-testid={`clock-review-row-${e.entry_id}`}
                    className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-semibold text-white/85">
                        {{ clock_in: "Clock in", clock_out: "Clock out", lunch_start: "Lunch start", lunch_end: "Lunch end", break_start: "Break start", break_end: "Break end", travel: "Travel", travel_end: "Arrived" }[e.event] || e.event}
                      </span>
                      {e.event === "clock_in" && (e.job_name || "") && (
                        <span className="block truncate font-mono text-[10px] text-white/40">{e.job_name}</span>
                      )}
                    </span>
                    <input
                      type="time"
                      value={reviewTimes[e.entry_id] || ""}
                      onChange={(ev) => setReviewTimes((m) => ({ ...m, [e.entry_id]: ev.target.value }))}
                      data-testid={`clock-review-time-${e.entry_id}`}
                      className="h-9 w-28 rounded border border-white/15 bg-white/5 px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                    />
                  </li>
                ))}
              </ul>
              {(today.events || []).length === 0 && (
                <p className="text-center font-mono text-[10px] uppercase tracking-widest text-white/40">No punches yet today</p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
              <button
                onClick={() => setReviewOpen(false)}
                data-testid="clock-review-not-done"
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
              >
                <Play className="h-3.5 w-3.5" /> Not done work
              </button>
              <button
                onClick={submitReview} disabled={reviewBusy || busy}
                data-testid="clock-review-save"
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-60"
              >
                {reviewBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />} Save &amp; sign out
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {outConfirm && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" data-testid="clock-out-confirm-dialog">
          <div className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <h3 className="text-lg font-bold uppercase tracking-tight">Clocked out</h3>
              <button onClick={() => setOutConfirm(null)} data-testid="clock-out-confirm-x" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 p-4">
              <p className="text-sm text-white/80" data-testid="clock-out-confirm-text">
                You clocked out at <span className="font-mono font-semibold text-white">{fmtClock(outConfirm.entry.timestamp)}</span>
                {" "}· {fmtHMS(outConfirm.paid)} paid today. Is that time right, or did you forget to clock out earlier?
              </p>
              {outFix && (
                <>
                  <input
                    type="time"
                    value={outTime}
                    onChange={(e) => setOutTime(e.target.value)}
                    data-testid="clock-out-fix-time"
                    className="h-11 w-full rounded border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                  />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    Pick the time you actually stopped working — today only
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
              {!outFix ? (
                <>
                  <button
                    onClick={() => { setOutFix(true); }}
                    data-testid="clock-out-forgot-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
                  >
                    <History className="h-3.5 w-3.5" /> I forgot — fix time
                  </button>
                  <button
                    onClick={() => setOutConfirm(null)}
                    data-testid="clock-out-correct-btn"
                    className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500"
                  >
                    Yes, that&apos;s right
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setOutFix(false)} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70" data-testid="clock-out-fix-cancel">Back</button>
                  <button
                    onClick={submitOutFix} disabled={busy}
                    data-testid="clock-out-fix-save"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />} Update clock-out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {oopsOpen && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" data-testid="clock-oops-dialog">
          <div className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <h3 className="text-lg font-bold uppercase tracking-tight">Forgot to clock in?</h3>
              <button onClick={() => setOopsOpen(false)} data-testid="clock-oops-cancel-x" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 p-4">
              <p className="text-sm text-white/80">
                What time did you get to site? Your timer will start from that time and your admin will be asked to confirm it.
              </p>
              <input
                type="time"
                value={oopsTime}
                onChange={(e) => setOopsTime(e.target.value)}
                data-testid="clock-oops-time"
                className="h-11 w-full rounded border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
              />
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Which job site were you at?</label>
                <select
                  value={oopsJobId}
                  onChange={(e) => setOopsJobId(e.target.value)}
                  data-testid="clock-oops-job"
                  className="h-11 w-full rounded border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none [&>option]:bg-white [&>option]:text-gray-900"
                >
                  <option value="">— Select a project —</option>
                  {jobs.map((j) => (
                    <option key={j.project_id} value={j.project_id}>{j.name}</option>
                  ))}
                </select>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Today only — you can&apos;t pick another date · the usual quarter-hour delay still applies
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
              <button onClick={() => setOopsOpen(false)} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70" data-testid="clock-oops-cancel">Cancel</button>
              <button
                onClick={submitOops} disabled={busy}
                data-testid="clock-oops-confirm"
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />} Start from this time
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ClockCard;
